/**
 * Pure projection of the selection flow's internal `phase` + `categoryIndex`
 * (SelectionFlowPage) onto the bottom step-bar model. NO store, NO React — a
 * plain function so it is unit-testable in isolation and the bar is a stateless
 * view of orchestrator state (plan §2).
 *
 * Bar steps (D2: ONE "Extra Protection" step spans every ancillary category):
 *   1 Welcome · 2 Health Plans* · 3 Extra Protection* · 4 Review · 5 Agreement ·
 *   6 Finish Up   (* omitted when that family isn't offered)
 *
 * "Health Plans" is a bar label only — the visible category heading keeps the
 * client-final "Health" copy (D4), so relabeling the bar changes no pinned string.
 */

export type StepState = 'completed' | 'active' | 'future';

export type StepKey =
  | 'welcome'
  | 'health'
  | 'extra'
  | 'review'
  | 'agreement'
  | 'finish';

export interface StepCell {
  /**
   * Stable identity so the orchestrator can map a bar click → navigation.
   * Optional: inert bars (the employee PHQ flow) have no click target.
   */
  key?: StepKey;
  label: string;
  state: StepState;
}

export interface StepModel {
  steps: StepCell[];
  activeIndex: number;
}

/** Categories are normalized upstream (`normalizeCategory`) → medical === 'Medical'. */
const isMedical = (category: string): boolean => category === 'Medical';

export type SelectionPhase =
  | 'welcome'
  | 'plans'
  | 'quote'
  | 'agreement'
  | 'done';

/**
 * @param categories   offered categories, de-duped + ordered (Medical → …).
 * @param phase        the orchestrator phase.
 * @param category     the category the plans phase is currently showing (ignored
 *                     off the plans phase).
 */
export function stepModel(
  categories: string[],
  phase: SelectionPhase,
  category?: string
): StepModel {
  const hasMedical = categories.some(isMedical);
  const hasAncillary = categories.some((c) => !isMedical(c));

  // The steps that EXIST for this offering set, in bar order.
  const present: { key: StepKey; label: string }[] = [
    { key: 'welcome', label: 'Welcome' },
  ];
  if (hasMedical) present.push({ key: 'health', label: 'Health Plans' });
  if (hasAncillary) present.push({ key: 'extra', label: 'Extra Protection' });
  present.push({ key: 'review', label: 'Review' });
  present.push({ key: 'agreement', label: 'Agreement' });
  present.push({ key: 'finish', label: 'Finish Up' });

  // The bar step the current phase maps to.
  let activeKey: StepKey;
  switch (phase) {
    case 'welcome':
      activeKey = 'welcome';
      break;
    case 'plans':
      // Zero-offering degenerate case (welcome → quote) still lands on Review.
      activeKey =
        categories.length === 0
          ? 'review'
          : category !== undefined && isMedical(category)
            ? 'health'
            : 'extra';
      break;
    case 'quote':
      activeKey = 'review';
      break;
    case 'agreement':
      activeKey = 'agreement';
      break;
    case 'done':
    default:
      activeKey = 'finish';
      break;
  }

  let activeIndex = present.findIndex((s) => s.key === activeKey);
  if (activeIndex === -1) activeIndex = 0;

  // Done: the whole journey reads as completed.
  const allCompleted = phase === 'done';

  const steps: StepCell[] = present.map((s, i) => ({
    key: s.key,
    label: s.label,
    state: allCompleted
      ? 'completed'
      : i < activeIndex
        ? 'completed'
        : i === activeIndex
          ? 'active'
          : 'future',
  }));

  return { steps, activeIndex };
}
